import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, ArrowRight, Network } from "lucide-react";
import FunderShell from "./FunderShell";
import { SectionCard, RemitPill, ConfidenceBar, scoreColor } from "./FunderUI";
import { getProposal, getProposals, getOverview } from "../../service/FunderPortalService";

const PRIORITY = { High: { bg: "var(--info-bg)", color: "var(--info)" }, Medium: { bg: "var(--warning-bg)", color: "var(--warning)" }, Low: { bg: "var(--surface-4)", color: "var(--ink-450)" } };

function genericSummary(p) {
    return `${p.title} is led by ${p.pi} at ${p.institution}. DeepThought places the work within "${p.cluster}", with Novelty ${p.novelty}, Methods ${p.methodology}, and Impact ${p.impact}. A full second-pass narrative is compiled once expert review begins.`;
}

export default function FunderProposal() {
    return (
        <FunderShell activePage="Submissions">
            {(callId, call) => <ProposalInner callId={callId} call={call} />}
        </FunderShell>
    );
}

function ProposalInner({ callId, call }) {
    const navigate = useNavigate();
    const location = useLocation();
    const id = new URLSearchParams(location.search).get("id");
    const p = getProposal(callId, id);

    if (!p) {
        return (
            <div className="funder-empty">
                <p>Proposal not found in this call.</p>
                <span className="funder-link" onClick={() => navigate("/funder/submissions")}>← Back to submissions</span>
            </div>
        );
    }

    const list = getProposals(callId);
    const idx = list.findIndex((x) => String(x.id) === String(p.id));
    const prev = idx > 0 ? list[idx - 1] : null;
    const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null;
    const overview = getOverview(callId);
    const avgByDim = overview?.metrics?.avgByDim ?? {};

    return (
        <div className="funder-section-stack">
            <div className="funder-proposal-topbar" style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span className="funder-link" onClick={() => navigate("/funder/submissions")}><ArrowLeft size={14} /> Back to submissions</span>
                <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
                    <button className="funder-btn-ghost funder-graph-btn" onClick={() => navigate("/sample/network")} style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}><Network size={14} /> Explore this proposal in the knowledge graph</button>
                    <button className="funder-btn-ghost" disabled={!prev} onClick={() => prev && navigate(`/funder/proposal?id=${prev.id}`)} style={{ opacity: prev ? 1 : 0.4 }}><ChevronLeft size={14} /></button>
                    <button className="funder-btn-ghost" disabled={!next} onClick={() => next && navigate(`/funder/proposal?id=${next.id}`)} style={{ opacity: next ? 1 : 0.4 }}><ChevronRight size={14} /></button>
                </div>
            </div>

            <div>
                <div className="funder-grant-no" style={{ marginBottom: 4 }}>{p.grantNumber}</div>
                <h1 className="funder-h1" style={{ color: "var(--ink-900)", margin: "0 0 6px" }}>{p.title}</h1>
                <div style={{ color: "var(--ink-500)", fontSize: 13.5 }}>{p.pi} · {p.institution} · Requested {p.amountRequested} over {p.durationYears} years</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                    {(p.tags ?? []).map((t, i) => <span key={t} className={`funder-tag${i === 0 ? " funder-tag-active" : ""}`}>{t}</span>)}
                </div>
            </div>

            <div className="funder-grid-proposal">
                {/* LEFT */}
                <div className="funder-section-stack">
                    <SectionCard kicker="DeepThought score card">
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
                            {call.criteria.map((c) => (
                                <div key={c.dim} style={{ border: "1px solid var(--border-subtle)", borderRadius: 12, padding: 14 }}>
                                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                                        <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink-600)" }}>{c.label}</span>
                                        <span style={{ fontSize: 30, fontWeight: 700, color: scoreColor(c.dim) }}>{p[c.dim]}</span>
                                    </div>
                                    <p style={{ fontSize: 11.5, color: "var(--ink-400)", margin: "4px 0 0", lineHeight: 1.4 }}>{c.text}</p>
                                    <ConfidenceBar value={p[c.dim]} band={p.conf?.[c.dim] ?? 0} color={scoreColor(c.dim)} callAverage={avgByDim[c.dim]} />
                                </div>
                            ))}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 8 }}>
                            <p style={{ fontSize: 11.5, fontStyle: "italic", color: "var(--ink-350)", margin: 0 }}>DeepThought scores are signals to focus reviewer attention, not decisions.</p>
                            <span className="funder-tag">Powered by DeepThought · 42Degrees</span>
                        </div>
                    </SectionCard>

                    {p.remit ? (
                        <SectionCard>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                                <RemitPill remit={p.remit} />
                                <span style={{ fontSize: 13, color: "var(--ink-600)", fontStyle: "italic" }}>{p.remit.explanation}</span>
                            </div>
                        </SectionCard>
                    ) : null}

                    {p.frontierSignal ? (
                        <div className="funder-card" style={{ borderColor: "var(--warning-border)", background: "var(--warm-bg)" }}>
                            <div className="funder-card-body">
                                <div className="funder-frontier-pill" style={{ marginBottom: 8 }}>Frontier signal</div>
                                <p style={{ fontSize: 13, color: "var(--warm-text)", lineHeight: 1.5, margin: "0 0 8px" }}>{p.frontierSignal.note}</p>
                                <div style={{ fontSize: 12.5, color: "var(--warm-text)", fontWeight: 600 }}>
                                    Novelty {p.frontierSignal.novelty} · Methods {p.frontierSignal.methodology} · Impact {p.frontierSignal.impact}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    <SectionCard kicker="DeepThought summary" title="Proposal overview">
                        {(p.summary ?? [genericSummary(p)]).map((para, i) => (
                            <p key={i} style={{ fontSize: 13.5, color: "var(--ink-700)", lineHeight: 1.6, margin: i === 0 ? "0 0 10px" : "0 0 10px" }}>{para}</p>
                        ))}
                    </SectionCard>

                    <SectionCard kicker="DeepThought analysis" title="Strengths & concerns" subtitle="Supplemented by written expert reviews when available.">
                        {p.strengths?.length || p.concerns?.length ? (
                            <>
                                {p.strengths?.length ? <div className="funder-kicker" style={{ marginBottom: 8 }}>Strengths</div> : null}
                                {(p.strengths ?? []).map((s, i) => <Finding key={`s${i}`} f={s} accent="var(--success)" />)}
                                {p.concerns?.length ? <div className="funder-kicker" style={{ margin: "14px 0 8px" }}>Concerns</div> : null}
                                {(p.concerns ?? []).map((c, i) => <Finding key={`c${i}`} f={c} accent="var(--series-amber)" />)}
                            </>
                        ) : (
                            <p style={{ fontSize: 13, color: "var(--ink-350)", margin: 0 }}>DeepThought analysis pending second-pass review.</p>
                        )}
                    </SectionCard>

                    {p.expertReviews?.length ? (
                        <SectionCard kicker="Expert reviews" title={`${p.expertReviews.length} expert ${p.expertReviews.length === 1 ? "review" : "reviews"} received`} className="" style={{ borderLeft: "3px solid #d4a017" }}>
                            {p.expertReviews.map((r, i) => (
                                <div key={i} style={{ marginBottom: 12 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-600)", marginBottom: 3 }}>{r.reviewer} · received {r.received}</div>
                                    <p style={{ fontSize: 13, color: "var(--ink-700)", lineHeight: 1.55, margin: 0 }}>{r.text}</p>
                                </div>
                            ))}
                            <p style={{ fontSize: 11.5, fontStyle: "italic", color: "var(--ink-350)", margin: "4px 0 0" }}>Expert reviews are confidential and unattributed per funder policy. Reviewers' identities are known only to program staff.</p>
                        </SectionCard>
                    ) : null}

                    {p.historicalAnalogues?.length ? (
                        <SectionCard kicker="Historical precedent" title="Similar previously funded work" subtitle="Closest DeepThought-similarity awards from the funded portfolio and their literature outcomes.">
                            {p.historicalAnalogues.map((a, i) => <Analogue key={i} a={a} />)}
                            {p.declinedAnalogue ? (
                                <div style={{ borderTop: "1px dashed var(--border)", marginTop: 12, paddingTop: 12 }}>
                                    <div className="funder-kicker" style={{ marginBottom: 6 }}>For calibration context</div>
                                    <Analogue a={p.declinedAnalogue} declined />
                                </div>
                            ) : null}
                        </SectionCard>
                    ) : null}

                    {p.similarProposals?.length ? (
                        <SectionCard kicker="Overlap & context" title="Within-call overlap">
                            {p.similarProposals.map((s, i) => {
                                const simColor = s.similarity > 70 ? "var(--series-amber)" : s.similarity < 50 ? "var(--success)" : "var(--ink-400)";
                                return (
                                    <div key={i} style={{ border: "1px solid var(--border-subtle)", borderRadius: 10, padding: 12, marginBottom: 8 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                                            <strong style={{ fontSize: 13, color: "var(--ink-900)" }}>{s.title}</strong>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: simColor, whiteSpace: "nowrap" }}>{s.similarity}%</span>
                                        </div>
                                        <div style={{ fontSize: 12, color: "var(--ink-400)", margin: "2px 0 6px" }}>{s.institution}</div>
                                        <div style={{ fontSize: 12.5, color: "var(--ink-600)" }}><strong>Overlap:</strong> {s.overlap}</div>
                                        <div style={{ fontSize: 12.5, color: "var(--ink-600)", marginTop: 2 }}><strong>Difference:</strong> {s.difference}</div>
                                    </div>
                                );
                            })}
                        </SectionCard>
                    ) : null}

                    {p.suggestedReviewers?.length ? (
                        <SectionCard kicker="DeepThought reviewer intelligence" title="Suggested expert reviewers" subtitle="Researchers surfaced through knowledge-graph proximity — expertise-matched and COI-screened.">
                            {p.suggestedReviewers.map((r, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--border-subtle)" }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-900)" }}>{r.name}</div>
                                        <div style={{ fontSize: 12, color: "var(--ink-400)" }}>{r.institution} · Expertise match: {r.match.toFixed ? r.match.toFixed(2) : r.match}</div>
                                        <div style={{ fontSize: 12.5, color: "var(--ink-600)", marginTop: 3 }}>{r.detail}</div>
                                        {r.note ? <div style={{ fontSize: 12, color: "var(--danger-muted)", marginTop: 3 }}>{r.note}</div> : null}
                                    </div>
                                    <span className="status-badge" style={{ background: r.badgeColor === "red" ? "var(--danger-bg)" : "var(--success-bg)", color: r.badgeColor === "red" ? "var(--danger)" : "var(--success)", flexShrink: 0 }}>{r.badge}</span>
                                    {r.suggestedBy === "deepthought" ? <button className="funder-invite-btn" onClick={() => {}}>Invite</button> : null}
                                </div>
                            ))}
                            <div style={{ marginTop: 10 }}>
                                <span className="funder-link" onClick={() => navigate("/funder/reviewer-management")}>View all matches on Reviewer Management <ArrowRight size={13} /></span>
                            </div>
                        </SectionCard>
                    ) : null}
                </div>

                {/* RIGHT — assessment form */}
                <div style={{ position: "sticky", top: 16 }}>
                    <AssessmentForm call={call} />
                </div>
            </div>
        </div>
    );
}

function Finding({ f, accent }) {
    const [open, setOpen] = useState(false);
    const pr = PRIORITY[f.priority] || PRIORITY.Low;
    return (
        <div style={{ borderLeft: `3px solid ${accent}`, paddingLeft: 12, marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="status-badge" style={{ background: pr.bg, color: pr.color }}>{f.priority}</span>
                <strong style={{ fontSize: 13, color: "var(--ink-900)" }}>{f.title}</strong>
            </div>
            <p style={{ fontSize: 12.5, color: "var(--ink-600)", margin: "4px 0 0", lineHeight: 1.5 }}>{f.description}</p>
            {f.evidence ? (
                <>
                    <span className="funder-link" style={{ fontSize: 12 }} onClick={() => setOpen((o) => !o)}>{open ? "Hide evidence" : "Show evidence"}</span>
                    {open ? <p style={{ fontSize: 12, fontStyle: "italic", color: "var(--ink-450)", margin: "4px 0 0", lineHeight: 1.5 }}>{f.evidence}</p> : null}
                </>
            ) : null}
        </div>
    );
}

function Analogue({ a, declined }) {
    return (
        <div style={{ border: "1px solid var(--border-subtle)", borderRadius: 10, padding: 12, marginBottom: 8, opacity: declined ? 0.85 : 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <strong style={{ fontSize: 13, color: "var(--ink-900)" }}>{a.title}</strong>
                <span style={{ fontSize: 11.5, color: "var(--ink-400)", whiteSpace: "nowrap" }}>sim {a.similarity.toFixed(2)}</span>
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-400)", margin: "2px 0" }}>{a.pi} · {a.funded}</div>
            <p style={{ fontSize: 12.5, color: "var(--ink-600)", margin: "4px 0 6px", lineHeight: 1.5 }}>{a.outcome}</p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(a.tags ?? []).map((t) => <span key={t} className="funder-tag">{t}</span>)}
            </div>
        </div>
    );
}

const CONFIDENCE_OPTS = ["Core expertise", "Adjacent field", "Peripheral"];

function AssessmentForm({ call }) {
    const [scores, setScores] = useState({ impact: null, novelty: null, methodology: null });
    const [just, setJust] = useState({ impact: "", novelty: "", methodology: "" });
    const [remitCheck, setRemitCheck] = useState("");
    const [remitReason, setRemitReason] = useState("");
    const [flags, setFlags] = useState({ coi: false, discuss: false, overlap: false, expertise: false });
    const [overlapId, setOverlapId] = useState("");
    const [confidence, setConfidence] = useState("");
    const [notes, setNotes] = useState("");
    const [saved, setSaved] = useState(false);

    const setScore = (dim, v) => { if (!flags.coi) { setScores((s) => ({ ...s, [dim]: v })); setSaved(false); } };

    return (
        <div className="funder-card">
            <div className="funder-card-header">
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <h2 className="funder-card-title">Your assessment</h2>
                        <span className="funder-tag">Draft</span>
                    </div>
                    <p className="funder-card-subtitle">Auto-saved · last edit just now</p>
                </div>
            </div>
            <div className="funder-card-body">
                {flags.coi ? (
                    <div style={{ background: "var(--danger-bg)", color: "var(--danger)", fontSize: 12.5, padding: "8px 10px", borderRadius: 8, marginBottom: 14 }}>
                        Conflict of interest declared. Scoring is disabled.
                    </div>
                ) : null}

                <div className="funder-kicker" style={{ marginBottom: 8 }}>Committee assessment</div>
                {call.criteria.map((c) => {
                    const warn = scores[c.dim] !== null && scores[c.dim] <= 4 && !just[c.dim];
                    return (
                        <div className="funder-form-field" key={c.dim}>
                            <label className="funder-form-label">{c.label}</label>
                            <p className="funder-form-help">{c.text}</p>
                            <div className="funder-circles">
                                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                                    <button
                                        key={n}
                                        className={`funder-circle${scores[c.dim] === n ? " funder-circle-active" : ""}`}
                                        onClick={() => setScore(c.dim, n)}
                                        disabled={flags.coi}
                                        style={{ opacity: flags.coi ? 0.5 : 1 }}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                            <input
                                className={`funder-input${warn ? " funder-input-warn" : ""}`}
                                style={{ marginTop: 8 }}
                                placeholder="Brief justification (required for scores ≤ 4)"
                                value={just[c.dim]}
                                disabled={flags.coi}
                                onChange={(e) => { setJust((j) => ({ ...j, [c.dim]: e.target.value })); setSaved(false); }}
                            />
                        </div>
                    );
                })}

                <div className="funder-form-field">
                    <label className="funder-form-label">Scope check</label>
                    <p className="funder-form-help">Does this proposal have a clearly defined link to human life, health and wellbeing?</p>
                    {[{ v: "in", l: "Clearly in scope" }, { v: "borderline", l: "Borderline — needs panel discussion" }, { v: "out", l: "Likely out of scope" }].map((o) => (
                        <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--ink-700)", marginBottom: 4, cursor: "pointer" }}>
                            <input type="radio" name="remitCheck" checked={remitCheck === o.v} onChange={() => { setRemitCheck(o.v); setSaved(false); }} />
                            {o.l}
                        </label>
                    ))}
                    {remitCheck === "out" ? (
                        <input className="funder-input" style={{ marginTop: 6 }} placeholder="Reason it falls outside scope" value={remitReason} onChange={(e) => setRemitReason(e.target.value)} />
                    ) : null}
                </div>

                <div className="funder-form-field">
                    <label className="funder-form-label">Flags</label>
                    {[
                        { k: "coi", l: "Conflict of interest", danger: true },
                        { k: "discuss", l: "Needs panel discussion" },
                        { k: "overlap", l: "Potential overlap with another submission" },
                        { k: "expertise", l: "Outside my core expertise" },
                    ].map((f) => (
                        <div className="funder-toggle-row" key={f.k}>
                            <span style={{ fontSize: 12.5, color: f.danger && flags[f.k] ? "var(--danger)" : "var(--ink-700)" }}>{f.l}</span>
                            <button
                                onClick={() => { setFlags((s) => ({ ...s, [f.k]: !s[f.k] })); setSaved(false); }}
                                aria-pressed={flags[f.k]}
                                style={{ position: "relative", width: 38, height: 20, borderRadius: 11, border: "none", cursor: "pointer", background: flags[f.k] ? (f.danger ? "#c62828" : "var(--brand)") : "var(--toggle-off-cool)" }}
                            >
                                <span style={{ position: "absolute", top: 2, left: flags[f.k] ? 20 : 2, width: 16, height: 16, borderRadius: "50%", background: "white", transition: "left 0.15s" }} />
                            </button>
                        </div>
                    ))}
                    {flags.overlap ? (
                        <input className="funder-input" style={{ marginTop: 8 }} placeholder="Overlapping submission ID (e.g. 042)" value={overlapId} onChange={(e) => setOverlapId(e.target.value)} />
                    ) : null}
                </div>

                <div className="funder-form-field">
                    <label className="funder-form-label">Confidence</label>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {CONFIDENCE_OPTS.map((o) => (
                            <button key={o} className={`funder-pill-toggle${confidence === o ? " funder-pill-toggle-active" : ""}`} onClick={() => { setConfidence(o); setSaved(false); }}>{o}</button>
                        ))}
                    </div>
                </div>

                <div className="funder-form-field">
                    <label className="funder-form-label">Notes for panel</label>
                    <textarea className="funder-input" rows={3} value={notes} onChange={(e) => { setNotes(e.target.value); setSaved(false); }} placeholder="Additional notes for the panel" />
                </div>

                <button className="funder-btn" style={{ width: "100%", justifyContent: "center" }} onClick={() => setSaved(true)}>Submit assessment</button>
                {saved ? <p style={{ fontSize: 12, color: "var(--success)", textAlign: "center", margin: "8px 0 0" }}>Saved (demo) — no data was sent.</p> : null}
                <p style={{ fontSize: 11.5, color: "var(--ink-350)", textAlign: "center", margin: "8px 0 0" }}>You can edit until panel lock date: {call.panelDate}</p>
            </div>
        </div>
    );
}
